import Button from 'tailwind-components/Button/Button';
import CarrierCard from './CarrierCard';
import CarrierDetail from './CarrierDetail';
import ResourcesFrame from './ResourcesFrame';
import CarrierResourcesByState from './CarrierResourcesByState';
import CarrierResourcesStickyHeader from './CarrierResourcesStickyHeader';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';
import axios from 'axios';
import propTypes from 'prop-types';
import { Loader2, Package } from 'lucide-react';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import SidebarPlaceholder from 'tailwind-components/ContentWithSidebar/SidebarPlaceholder';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import CarrierDetailStates from './CarrierDetailStates';
import GroupedCarrierSections from 'tailwind-components/GroupedCarrierSections/GroupedCarrierSections';
import {
  APPOINTED_CARRIERS_TAB_ID,
  getAppointedCarrierNameSet,
  getCarrierParentGroupTabIds,
  getGroupedCarriersByParent,
  normalizeStatesOfferedToCodes
} from 'helpers/carriers/carriers';
import { isAgentAppointedWithCarrier } from 'helpers/appointments/appointments';
import { useSelector } from 'react-redux';
import useNotifyTourPageReady from 'tailwind-components/PortalWalkthrough/useNotifyTourPageReady';
import useTabUrlState from 'hooks/useTabUrlState';

const CARRIER_GRID_SUBTITLE =
  'Please click on a carrier icon below to view more information.';
const BY_STATE_SUBTITLE =
  'Please click on a state below to view more information';

function CarrierResources({
  carriers,
  appointedCarriers,
  carrierFaqs,
  getAppointmentsViewData,
  getCarrierFaqs,
  setLocalLoader,
  carrierLoader,
  tabActive = true
}) {
  const navigate = useNavigate();
  const statesSectionRef = useRef(null);
  const cardsScrollRef = useRef(null);
  const getAppointedRef = useRef(null);
  const [message, setMessage] = useState('');
  const [active, setActive] = useState({ name: '', type: '' });
  const [currentCarrier, setCurrentCarrier] = useState({});
  const [carrierStates, setCarrierStates] = useState([]);
  const [appointmentsDisabled, setAppointmentsDisabled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // local loading state so the carriers grid shows its own skeleton (the parent
  // loader is not always wired, e.g. inside the Resources tabs)
  const [carriersLoading, setCarriersLoading] = useState(true);
  const cancelTokenSource = axios.CancelToken.source();
  const { carriersArray } = useSelector(state => state.appointments);

  // tab synced to the URL (param scoped so it doesn't collide with the Resources outer ?tab)
  const carrierTabIds = useMemo(
    () => [
      APPOINTED_CARRIERS_TAB_ID,
      ...getCarrierParentGroupTabIds(carriersArray),
      'by-state'
    ],
    [carriersArray]
  );
  const [currentTab, setCurrentTab] = useTabUrlState({
    param: 'carrierTab',
    tabIds: carrierTabIds,
    defaultTabId: APPOINTED_CARRIERS_TAB_ID,
    active: tabActive
  });
  const user = useSelector(state => state.user.zohoData);
  const isBelowLg = useBelowBreakpoint('lg');

  const filteredCarriersArray = useMemo(
    () =>
      carriersArray.filter(carrier => {
        const isAppointed = isAgentAppointedWithCarrier(carrier.name, user);
        return !carrier.appointmentsDisabled || isAppointed;
      }),
    [carriersArray, user]
  );

  const appointedCarrierNames = useMemo(
    () => getAppointedCarrierNameSet(appointedCarriers),
    [appointedCarriers]
  );

  const groupedCarriers = useMemo(
    () =>
      getGroupedCarriersByParent(
        filteredCarriersArray,
        currentTab,
        appointedCarrierNames
      ),
    [filteredCarriersArray, currentTab, appointedCarrierNames]
  );

  useEffect(() => {
    getAppointmentsViewData(
      value => {
        setLocalLoader(value);
        setCarriersLoading(value);
      },
      cancelTokenSource,
      true
    );
    return () => cancelTokenSource.cancel('API Call Canceled');
  }, []);

  useNotifyTourPageReady(carriersLoading);

  useEffect(() => {
    if (active.name) {
      setMessage('No information available for this carrier');
      let carrierData = {};
      for (const group of Object.values(carriers || {})) {
        if (group?.[active.name]) {
          carrierData = group[active.name];
          break;
        }
      }
      if (!carrierData.id) {
        for (const group of Object.values(appointedCarriers || {})) {
          if (group?.[active.name]) {
            carrierData = group[active.name];
            break;
          }
        }
      }
      setCurrentCarrier(carrierData);
      const matched = carriersArray.find(c => c.name === active.name);
      setCarrierStates(normalizeStatesOfferedToCodes(matched?.statesOffered));
      setAppointmentsDisabled(!!matched?.appointmentsDisabled);
    } else {
      setCarrierStates([]);
      setAppointmentsDisabled(false);
    }
  }, [active.name]);

  useEffect(() => {
    if (carrierStates.length > 0 && cardsScrollRef.current) {
      setTimeout(() => {
        cardsScrollRef.current.scrollTo({
          top: cardsScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 300);
    }
  }, [carrierStates]);

  useEffect(() => {
    if (currentCarrier.id) {
      getCarrierFaqs(currentCarrier.id, cancelTokenSource);
      return () => cancelTokenSource.cancel('API Call Canceled');
    }
  }, [currentCarrier]);

  const handleResetActiveCarrier = () => {
    setActive({ name: '', type: '' });
    setSidebarOpen(false);
  };

  const handleSetActive = value => {
    setActive(value);
    if (value.name) setSidebarOpen(true);
  };

  const hasCarrierInfo =
    active.type &&
    (currentCarrier.carrierBrokerPortal ||
      currentCarrier.brokerSupportNumber ||
      currentCarrier.memberSupportNumber ||
      currentCarrier.description ||
      currentCarrier.enrollmentPortal ||
      currentCarrier.omWebsiteLink ||
      currentCarrier.workDriveFolderURL);

  const hasWorkDrive = Boolean(currentCarrier?.workDriveFolderURL);

  const hasCarrierDetailSections = useMemo(
    () =>
      Boolean(
        currentCarrier?.description ||
          currentCarrier?.brokerSupportNumber ||
          currentCarrier?.memberSupportNumber ||
          currentCarrier?.Broker_Support_Email ||
          currentCarrier?.Broker_Commission_Email ||
          currentCarrier?.username ||
          currentCarrier?.carrierBrokerPortal ||
          currentCarrier?.enrollmentPortal ||
          currentCarrier?.omWebsiteLink ||
          (carrierFaqs && carrierFaqs.length > 0)
      ),
    [currentCarrier, carrierFaqs]
  );

  const mainContent = (
    <SimpleContainer className='relative h-full min-h-0 gap-4'>
      <CarrierResourcesStickyHeader
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        handleResetActiveCarrier={handleResetActiveCarrier}
      />

      <div
        ref={cardsScrollRef}
        data-tour='carrier-resources-grid'
        className='show-scrollbar flex min-h-0 min-w-0 w-full flex-1 flex-col gap-4 overflow-y-auto rounded-xl bg-white p-4'
      >
        {currentTab === 'by-state' ? (
          <>
            <p className='m-0 text-center text-sm text-gray-600'>
              {BY_STATE_SUBTITLE}
            </p>
            <CarrierResourcesByState
              selectedCarrier={active.name}
              onCarrierClick={name =>
                handleSetActive({ name, type: name ? 'by-state' : '' })
              }
            />
          </>
        ) : carriersLoading ? (
          <div className='flex flex-1 flex-col items-center justify-center gap-3 py-10'>
            <Loader2
              aria-hidden
              className='size-8 shrink-0 animate-spin text-primary'
            />
            <p className='m-0 text-base font-medium text-gray-800'>
              Loading carriers...
            </p>
          </div>
        ) : groupedCarriers.length === 0 ? (
          <p className='mt-10 text-center text-sm text-gray-400'>
            No carriers available
          </p>
        ) : (
          <div>
            <SimpleContainer className='gap-6'>
              <GroupedCarrierSections
                groupedCarriers={groupedCarriers}
                subtitle={CARRIER_GRID_SUBTITLE}
                filterLegendsForGrid={false}
                sectionHeaderClassName='mb-3 mt-3'
                renderMainGrid={(carriers, { parentGroupName }) => (
                  <div className='equal-rows simple-carrier-grid mb-6 grid gap-1.5'>
                    {carriers.map(carrier => (
                      <div
                        key={carrier.name}
                        className='flex min-h-[90px] min-w-0 w-full h-full'
                      >
                        <CarrierCard
                          name={carrier.name}
                          type={parentGroupName}
                          carrierIcon={carrier.carrierIcon}
                          carrierIconColor={carrier.carrierIconColor}
                          active={active.name}
                          setActive={handleSetActive}
                          className='h-full w-full'
                        />
                      </div>
                    ))}
                  </div>
                )}
                renderSubgroupGrid={(subCarriers, { parentGroupName }) => (
                  <div className='equal-rows simple-carrier-grid grid gap-3'>
                    {subCarriers.map(carrier => (
                      <div
                        key={carrier.name}
                        className='flex min-h-[90px] min-w-0 w-full h-full'
                      >
                        <CarrierCard
                          name={carrier.name}
                          type={parentGroupName}
                          carrierIcon={carrier.carrierIcon}
                          carrierIconColor={carrier.carrierIconColor}
                          active={active.name}
                          setActive={handleSetActive}
                          className='h-full w-full'
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
            </SimpleContainer>
          </div>
        )}

        {carrierStates.length > 0 && currentTab !== 'by-state' && (
          <>
            <hr
              ref={statesSectionRef}
              className='m-0 border-dashed border-gray-200'
            />
            <CarrierDetailStates
              states={carrierStates}
              carrierName={active.name}
              ignoreSbeAndFfmLabels={currentCarrier.ignoreSbeAndFfmLabels}
            />
            <div ref={getAppointedRef} className='flex justify-center mt-5'>
              <Button
                variant={appointmentsDisabled ? 'default' : 'success'}
                disabled={appointmentsDisabled}
                text='Get Appointed'
                size='medium'
                onClick={() =>
                  navigate(`/carrier-appointments?carrier=${active.name}`)
                }
              />
            </div>
          </>
        )}
      </div>
    </SimpleContainer>
  );

  const detailSection = hasCarrierDetailSections ? (
    <div
      className={`show-scrollbar overflow-y-auto bg-white p-3 lg:rounded-b-xl lg:border lg:border-[#d9d9da] ${
        hasWorkDrive ? 'max-h-[50%] flex-[1_1_50%]' : 'flex-1'
      }`}
    >
      <CarrierDetail
        carrier={currentCarrier}
        carrierFaqs={carrierFaqs}
        carrierLoader={carrierLoader}
      />
    </div>
  ) : null;

  const resourcesSection = hasWorkDrive ? (
    <div className='min-h-0 flex-1 overflow-hidden bg-white border-t border-[#d9d9da] lg:border lg:rounded-xl'>
      <ResourcesFrame carrier={currentCarrier} layout='full' />
    </div>
  ) : null;

  const sidebarContent = active.name ? (
    <div
      data-tour='carrier-resources-sidebar'
      className='flex h-full min-h-0 w-full flex-col overflow-hidden border border-[#d9d9da] lg:border-0 lg:gap-3 lg:rounded-none'
    >
      {hasCarrierInfo ? (
        <>
          {detailSection}
          {resourcesSection}
        </>
      ) : (
        <div className='flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center'>
          <Package size={40} className='text-gray-300' />
          <p className='m-0 text-sm text-gray-400'>{message}</p>
        </div>
      )}
    </div>
  ) : (
    <SidebarPlaceholder text='Select a carrier to view details' />
  );

  return (
    <div className='w-full h-full'>
      <ContentWithSidebar
        mainContent={mainContent}
        sidebarContent={sidebarContent}
        sidebarAsSheetOnMobile
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={open => {
          if (!open) {
            if (isBelowLg) setSidebarOpen(false);
            else handleResetActiveCarrier();
          }
        }}
        sidebarTitle={active.name || ''}
        handleCloseSidebar={() => {
          if (isBelowLg) {
            setSidebarOpen(false);
            return;
          }
          handleResetActiveCarrier();
        }}
        desktopHeaderClassName='bg-white rounded-t-xl'
        desktopSidebarGridClassName='lg:grid-cols-[minmax(0,1fr)_400px]'
      />
    </div>
  );
}

CarrierResources.propTypes = {
  tabActive: propTypes.bool,
  carriers: propTypes.object,
  appointedCarriers: propTypes.object,
  carrierFaqs: propTypes.array,
  getAppointmentsViewData: propTypes.func,
  getCarrierFaqs: propTypes.func,
  setLocalLoader: propTypes.func,
  carrierLoader: propTypes.bool
};

export default CarrierResources;
